import { isFcrActivated, isResponded } from './deliverySummaryV1';
import { marketsWithCommitment } from './marketReportsV1';
import { DAY_MS, utcDayStart } from './recordWindow';

/**
 * Day and week aggregates for the 30 d / 90 d delivery dashboard.
 *
 * At a month or a quarter the question is no longer "what happened in this quarter-hour" but
 * "every day, how did it go" and "what did we sell each week and what became of it". Both are
 * answered from one dense set of day aggregates — one per UTC day of the window, empty days
 * included — so the calendar, the weekly bars, the stat tiles and the summary line all read
 * the same numbers. Every rule here is the one `summarizeDelivery` applies to the tiles,
 * restated per day, so that `summarizeDays(aggregateDays(reports))` is the same figure as
 * `summarizeDelivery(reports)` (tested).
 *
 * @typedef {object} DayAggregate
 * @property {number} day UTC midnight, ms
 * @property {number} periods settled reports filed under this day (by `start`)
 * @property {number} measured
 * @property {number} outOfService
 * @property {number} unmeasured
 * @property {number} marketReports reports carrying a market report at all
 * @property {number} committedMwSum Σ `committedPower` over `marketReports`
 * @property {number} committed tile rule: `committedPower !== 0` and not unmeasured
 * @property {number} committedOutOfService of `committed`, settled with every member disabled
 * @property {number} committedUnmeasured committed but unmeasured — outside `committed`
 * @property {number} backed of `committed`: `availablePowerMin >= committedPower`
 * @property {number} activated measured periods in which the FCR service was activated (`isFcrActivated`)
 * @property {number} responded of `activated`, periods whose score exceeds the responded bar
 * @property {number} missed of `activated`, the rest
 * @property {number} standby backed and never activated
 * @property {number} respondedBacked of `backed`, activated and responded
 * @property {number} missedBacked of `backed`, activated and missed
 * @property {number} capacityHours Σ `committedPower` × period hours over `committed` — MW·h sold
 * @property {number} respondedHours
 * @property {number} standbyHours
 * @property {number} missedHours
 * @property {number} notBackedHours
 * @property {number|null} firstEnd
 * @property {number|null} lastEnd
 */

export const emptyDay = day => ({
    day,
    periods: 0,
    measured: 0,
    outOfService: 0,
    unmeasured: 0,
    marketReports: 0,
    committedMwSum: 0,
    committed: 0,
    committedOutOfService: 0,
    committedUnmeasured: 0,
    backed: 0,
    activated: 0,
    responded: 0,
    missed: 0,
    standby: 0,
    respondedBacked: 0,
    missedBacked: 0,
    capacityHours: 0,
    respondedHours: 0,
    standbyHours: 0,
    missedHours: 0,
    notBackedHours: 0,
    firstEnd: null,
    lastEnd: null,
});

// The tile's "backed" test on one report.
const isBacked = r => {
    const m = r.markets;
    return m !== null && m.availablePowerMin !== null && m.availablePowerMin >= m.committedPower;
};

const periodHours = r => {
    const h = (Date.parse(r.end) - Date.parse(r.start)) / 3600000;
    return Number.isFinite(h) && h > 0 ? h : 0;
};

const aggregateBucket = (day, bucket, committedMarkets) => {
    const d = emptyDay(day);
    d.periods = bucket.length;
    for (const r of bucket) {
        if (r.measurement === 'measured') {
            d.measured += 1;
        } else if (r.measurement === 'out_of_service') {
            d.outOfService += 1;
        } else {
            d.unmeasured += 1;
        }

        const end = Date.parse(r.end);
        if (Number.isFinite(end)) {
            d.firstEnd = d.firstEnd === null ? end : Math.min(d.firstEnd, end);
            d.lastEnd = d.lastEnd === null ? end : Math.max(d.lastEnd, end);
        }

        // One activation notion everywhere: the per-market figure with the legacy
        // fallback (`isFcrActivated`). Responded is a share of the activated periods.
        const activated = isFcrActivated(r, committedMarkets);
        const responded = activated && isResponded(r);
        if (activated) {
            d.activated += 1;
            if (responded) {
                d.responded += 1;
            } else {
                d.missed += 1;
            }
        }

        const m = r.markets;
        if (m === null) {
            continue;
        }
        d.marketReports += 1;
        d.committedMwSum += m.committedPower;

        if (m.committedPower === 0) {
            continue;
        }
        if (r.measurement === 'unmeasured') {
            d.committedUnmeasured += 1;
            continue;
        }
        d.committed += 1;
        if (r.measurement === 'out_of_service') {
            d.committedOutOfService += 1;
        }
        const hours = m.committedPower * periodHours(r);
        d.capacityHours += hours;
        if (!isBacked(r)) {
            d.notBackedHours += hours;
            continue;
        }
        d.backed += 1;
        // An activated-but-unbacked period is in `activated` / `responded` / `missed` for
        // the calendar, but its hours are `notBacked` above: the stack answers "what became
        // of the capacity we sold", and capacity that was not there cannot have responded.
        if (activated && responded) {
            d.respondedBacked += 1;
            d.respondedHours += hours;
        } else if (activated) {
            d.missedBacked += 1;
            d.missedHours += hours;
        } else {
            d.standby += 1;
            d.standbyHours += hours;
        }
    }
    return d;
};

/**
 * One aggregate per UTC day over `[fromMs, toMs]`, in order, empty days included. Reports are
 * filed by `start`: the 23:45–00:00 period belongs to the day containing 23:45. Reports outside
 * the window are dropped.
 *
 * @param {import('./marketReportsV1').Delivered[]} reports
 * @param {number} fromMs
 * @param {number} toMs
 * @returns {DayAggregate[]}
 */
export const aggregateDays = (reports, fromMs, toMs) => {
    // Window-level, matching `summarizeDelivery`: which markets the battery sold into
    // anywhere in the window scopes what "activated" means on every day of it.
    const committedMarkets = marketsWithCommitment(reports);
    const byDay = new Map();
    for (const r of reports) {
        const day = utcDayStart(Date.parse(r.start));
        if (!Number.isFinite(day)) {
            continue;
        }
        const bucket = byDay.get(day);
        if (bucket) {
            bucket.push(r);
        } else {
            byDay.set(day, [r]);
        }
    }
    const days = [];
    const lastDay = utcDayStart(toMs);
    for (let day = utcDayStart(fromMs); day <= lastDay; day += DAY_MS) {
        const bucket = byDay.get(day);
        days.push(bucket ? aggregateBucket(day, bucket, committedMarkets) : emptyDay(day));
    }
    return days;
};

/** Monday 00:00 UTC of the ISO week containing `day`. */
export const weekStart = day => {
    const dow = (new Date(day).getUTCDay() + 6) % 7;
    return utcDayStart(day) - dow * DAY_MS;
};

/**
 * Group day aggregates by ISO week (Monday, UTC), preserving order.
 *
 * @param {DayAggregate[]} days
 * @returns {Array<{week: number, days: DayAggregate[], capacityHours: number, respondedHours: number, standbyHours: number, missedHours: number, notBackedHours: number, respondedShare: number|null}>}
 */
export const groupWeeks = days => {
    const weeks = [];
    for (const d of days) {
        const week = weekStart(d.day);
        let w = weeks[weeks.length - 1];
        if (!w || w.week !== week) {
            w = { week, days: [], capacityHours: 0, respondedHours: 0, standbyHours: 0, missedHours: 0, notBackedHours: 0, respondedShare: null };
            weeks.push(w);
        }
        w.days.push(d);
        w.capacityHours += d.capacityHours;
        w.respondedHours += d.respondedHours;
        w.standbyHours += d.standbyHours;
        w.missedHours += d.missedHours;
        w.notBackedHours += d.notBackedHours;
    }
    for (const w of weeks) {
        // The week's responded share: of its activated periods, how many cleared the score
        // bar — the same figure the Responded tile states, per week and in percent.
        const activated = w.days.reduce((a, d) => a + d.activated, 0);
        const responded = w.days.reduce((a, d) => a + d.responded, 0);
        w.respondedShare = activated > 0 ? (responded / activated) * 100 : null;
    }
    return weeks;
};

/**
 * The tiles' figures from a day set — the same shape and rules as `summarizeDelivery`.
 * `product` is `committedProduct(reports)`: the product is read off the reports' market
 * fields, which a day aggregate no longer carries. Null when the set holds no report at all.
 *
 * @param {DayAggregate[]} days
 * @param {{label: string, color: string}|null} product
 * @returns {import('./deliverySummaryV1').DeliverySummary|null}
 */
export const summarizeDays = (days, product) => {
    const sum = f => days.reduce((a, d) => a + f(d), 0);
    const reportCount = sum(d => d.periods);
    if (reportCount === 0) {
        return null;
    }
    const marketReportCount = sum(d => d.marketReports);
    const committedCount = sum(d => d.committed);
    const backedCount = sum(d => d.backed);
    const activatedCount = sum(d => d.activated);
    const respondedCount = sum(d => d.responded);
    const firstEnds = days.flatMap(d => (d.firstEnd === null ? [] : [d.firstEnd]));
    const lastEnds = days.flatMap(d => (d.lastEnd === null ? [] : [d.lastEnd]));
    return {
        meanCommittedMw: marketReportCount === 0 ? 0 : sum(d => d.committedMwSum) / marketReportCount,
        committedProduct: product,
        marketReportCount,
        committedCount,
        backedCount,
        committedOutOfServiceCount: sum(d => d.committedOutOfService),
        committedUnmeasuredCount: sum(d => d.committedUnmeasured),
        backedRate: committedCount > 0 ? backedCount / committedCount : null,
        activatedCount,
        respondedCount,
        respondedRate: activatedCount > 0 ? respondedCount / activatedCount : null,
        measuredCount: sum(d => d.measured),
        outOfServiceCount: sum(d => d.outOfService),
        unmeasuredCount: sum(d => d.unmeasured),
        reportCount,
        firstEnd: Math.min(...firstEnds),
        lastEnd: Math.max(...lastEnds),
    };
};
